import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Camera, CheckCircle2, XCircle, Clock, AlertTriangle, MapPin, RefreshCw,
  Filter, User2, Pencil, Save,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const FLAG_META = {
  very_late:       { icon: Clock,          color: "amber", label: "Telat >30 menit" },
  no_photo_in:     { icon: Camera,         color: "rose",  label: "Tanpa selfie masuk" },
  no_photo_out:    { icon: Camera,         color: "rose",  label: "Tanpa selfie pulang" },
  radius_override: { icon: MapPin,         color: "violet",label: "Override radius" },
};

export default function AttendanceReview() {
  const { user, can } = useAuth();
  const canEditClock = user?.role === "superadmin" || user?.role === "admin" || can?.("attendance_edit", "update");
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { att, photos }
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Edit clock modal
  const [editModal, setEditModal] = useState(null); // { att }
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", note: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  // Urutan bawaan (dari backend) dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();

  // Nomor urut request: klik filter status beruntun (atau Refresh saat request
  // sebelumnya masih jalan) tidak boleh membiarkan respons lama menimpa daftar
  // tab aktif — baris "Ditolak" bisa nongol di tab "Pending Review". Hanya
  // respons request TERAKHIR yang boleh menyentuh state.
  const reqSeq = useRef(0);
  const load = () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    const p = new URLSearchParams({ status });
    api.get(`/attendance/flagged?${p}`)
      .then(r => { if (seq === reqSeq.current) setItems(r.data); })
      .catch(e => { if (seq === reqSeq.current) toast.error(e?.response?.data?.detail || "Gagal load"); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const openDetail = async (att) => {
    setDetail({ att, photos: null });
    setNote("");
    try {
      const { data } = await api.get(`/attendance/${att.id}/photos`);
      setDetail({ att, photos: data });
    } catch (e) { toast.error("Gagal load foto"); }
  };

  const review = async (decision) => {
    if (!detail) return;
    setSaving(true);
    try {
      await api.post(`/attendance/${detail.att.id}/review`, { decision, note });
      toast.success(decision === "approved" ? "Absensi disetujui" : "Absensi ditolak");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally { setSaving(false); }
  };

  // Convert ISO datetime → HH:MM (WIB)
  const isoToHHMM = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const wib = new Date(d.getTime() + (7 * 60 - d.getTimezoneOffset()) * 60000);
      return `${String(wib.getUTCHours()).padStart(2, "0")}:${String(wib.getUTCMinutes()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  const openEditClock = async (att) => {
    setEditModal({ att });
    setEditForm({
      clock_in: isoToHHMM(att.clock_in),
      clock_out: isoToHHMM(att.clock_out),
      note: "",
    });
    setEditHistory([]);
    try {
      const { data } = await api.get(`/attendance/${att.id}/edit-history`);
      setEditHistory(data || []);
    } catch { /* ignore */ }
  };

  const saveEditClock = async () => {
    if (!editModal) return;
    if (!editForm.note.trim()) {
      toast.error("Catatan alasan wajib diisi untuk audit trail");
      return;
    }
    setEditSaving(true);
    try {
      const payload = { note: editForm.note };
      // Only include fields when non-empty OR explicitly cleared (send "")
      payload.clock_in = editForm.clock_in;
      payload.clock_out = editForm.clock_out;
      const { data } = await api.patch(`/attendance/${editModal.att.id}/edit-clock`, payload);
      toast.success(data.changes?.length
        ? `Jam absensi tersimpan (${data.changes.length} perubahan)`
        : "Tidak ada perubahan");
      setEditModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setEditSaving(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">HRIS · Review Selfie</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Camera className="w-6 h-6 text-emerald-600" /> Review Selfie Absensi
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Absensi dengan anomali (telat &gt;30 mnt, tanpa selfie, override radius) di-flag otomatis untuk review HR.
          </p>
        </div>
        <button onClick={load} data-testid="btn-refresh-review"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {/* Filter */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-semibold text-slate-600 mr-1">Status:</span>
        {["pending", "approved", "rejected", "all"].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-${s}`}
            className={`h-8 px-3 rounded-md text-xs font-medium ${status === s
              ? "bg-emerald-600 text-white"
              : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
            {s === "pending" ? "Pending Review" : s === "approved" ? "Disetujui" : s === "rejected" ? "Ditolak" : "Semua"}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">{items.length} item</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Karyawan" sortKeyName="employee_nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="review-sort-karyawan" />
              <SortTh label="Tgl" sortKeyName="date" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="review-sort-tgl" />
              <SortTh label="Clock In" sortKeyName="clock_in" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="review-sort-clockin" />
              <th className="text-left py-3 px-4">Anomali</th>
              <th className="text-center py-3 px-4">Foto</th>
              <SortTh label="Status" sortKeyName="review_status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="review-sort-status" />
            </tr>
          </thead>
          <tbody>
            {sortRows(items, sortKey, sortDir).map(a => (
              <tr key={a.id} onClick={() => openDetail(a)}
                data-testid={`review-row-${a.id}`}
                className="border-b border-slate-100 hover:bg-emerald-50/30 cursor-pointer">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 grid place-items-center text-emerald-700 text-xs font-bold">
                      {(a.employee_nama || "?")[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{a.employee_nama}</p>
                      <p className="text-[11px] text-slate-500">{a.branch || "—"}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4 font-mono text-xs">{a.date}</td>
                <td className="py-3 px-4 text-xs">
                  {a.clock_in ? new Date(a.clock_in).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "-"}
                  {a.late_minutes > 0 && <span className="ml-1 text-amber-600 font-medium">+{a.late_minutes}mnt</span>}
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">
                    {(a.flags || []).map(f => {
                      const m = FLAG_META[f] || { label: f, color: "slate" };
                      const Icon = m.icon || AlertTriangle;
                      return (
                        <span key={f} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-${m.color}-100 text-${m.color}-800`}>
                          <Icon className="w-2.5 h-2.5" /> {m.label}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="py-3 px-4 text-center text-xs">
                  {a.has_photo_in ? "📷 IN" : ""} {a.has_photo_out ? "📷 OUT" : ""}
                  {!a.has_photo_in && !a.has_photo_out && <span className="text-slate-300">—</span>}
                </td>
                <td className="py-3 px-4 text-right">
                  {a.review_status === "approved" ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">✓ Approved</span>
                  ) : a.review_status === "rejected" ? (
                    <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-bold">✕ Rejected</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">⏳ Pending</span>
                  )}
                  {canEditClock && (
                    <button
                      onClick={(e) => { e.stopPropagation(); openEditClock(a); }}
                      data-testid={`btn-edit-clock-${a.id}`}
                      title="Edit jam masuk/pulang (bypass SPV)"
                      className="ml-2 p-1.5 rounded hover:bg-violet-100 text-slate-400 hover:text-violet-700 inline-flex align-middle"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <LoadingState
                loading={loading}
                kosong={!loading}
                pesanKosong="Tidak ada absensi yang perlu direview"
                varian="tabel"
                kolom={6}
                data-testid="review-loading"
              />
            )}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="review-modal">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="font-display font-semibold">Review Absensi</h3>
                <p className="text-[11px] text-emerald-100">
                  {detail.att.employee_nama} · {detail.att.date}
                  {detail.att.late_minutes > 0 && <span> · <b>telat {detail.att.late_minutes} mnt</b></span>}
                </p>
              </div>
              <button onClick={() => setDetail(null)}
                className="p-1.5 hover:bg-white/20 rounded-lg"><XCircle className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-4">
              {detail.photos ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Selfie Clock In</p>
                    {detail.photos.clock_in_photo ? (
                      <img src={`data:image/*;base64,${detail.photos.clock_in_photo}`}
                        data-testid="review-photo-in"
                        className="w-full rounded-lg border border-slate-200 object-cover aspect-square" />
                    ) : (
                      <div className="w-full aspect-square rounded-lg border-2 border-dashed border-rose-300 grid place-items-center text-xs text-rose-500">
                        Tidak ada foto ⚠️
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-600 mb-1">Selfie Clock Out</p>
                    {detail.photos.clock_out_photo ? (
                      <img src={`data:image/*;base64,${detail.photos.clock_out_photo}`}
                        data-testid="review-photo-out"
                        className="w-full rounded-lg border border-slate-200 object-cover aspect-square" />
                    ) : (
                      <div className="w-full aspect-square rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-xs text-slate-400">
                        Belum clock-out
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-40"><LoadingState loading jumlahKartu={1} data-testid="review-foto-loading" /></div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Flag Terdeteksi</p>
                <div className="flex flex-wrap gap-1">
                  {(detail.att.flags || []).map(f => {
                    const m = FLAG_META[f] || { label: f };
                    return <span key={f} className="text-xs px-2 py-0.5 rounded-full bg-white text-amber-800 border border-amber-300">{m.label}</span>;
                  })}
                </div>
              </div>

              <label className="block">
                <span className="text-xs font-medium text-slate-600">Catatan Review</span>
                <textarea value={note} onChange={e => setNote(e.target.value)}
                  data-testid="review-note"
                  placeholder="Contoh: sudah konfirmasi ke sales, override valid karena survei lapangan…"
                  className="mt-1 w-full h-20 p-2 rounded-lg border border-slate-200 text-sm resize-none" />
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => review("rejected")} disabled={saving}
                  data-testid="btn-reject"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-medium disabled:opacity-50">
                  <XCircle className="w-4 h-4" /> Tolak
                </button>
                <button onClick={() => review("approved")} disabled={saving}
                  data-testid="btn-approve"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                  <CheckCircle2 className="w-4 h-4" /> Setujui
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Clock Modal (bypass SPV/Admin) */}
      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setEditModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="edit-clock-modal">
            <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white px-5 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">Edit Jam Absensi (Bypass)</h3>
                  <p className="text-[11px] text-violet-100">
                    {editModal.att.employee_nama} · {editModal.att.date}
                  </p>
                </div>
              </div>
              <button onClick={() => setEditModal(null)} className="p-1.5 hover:bg-white/20 rounded-lg">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-800">
                <AlertTriangle className="w-3.5 h-3.5 inline mb-0.5 mr-1" />
                Ubah jam clock-in/out akan tercatat di audit log &amp; notifikasi ke karyawan.
                Format: <b>HH:MM</b> (WIB). Kosongkan untuk hapus jam.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Clock-In (WIB)</span>
                  <input type="time" value={editForm.clock_in}
                    onChange={e => setEditForm(s => ({ ...s, clock_in: e.target.value }))}
                    data-testid="edit-clock-in"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Sebelumnya: {isoToHHMM(editModal.att.clock_in) || "—"}</p>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Clock-Out (WIB)</span>
                  <input type="time" value={editForm.clock_out}
                    onChange={e => setEditForm(s => ({ ...s, clock_out: e.target.value }))}
                    data-testid="edit-clock-out"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
                  <p className="text-[10px] text-slate-400 mt-0.5">Sebelumnya: {isoToHHMM(editModal.att.clock_out) || "—"}</p>
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">
                  Catatan Alasan <span className="text-rose-500">*</span>
                </span>
                <textarea value={editForm.note} onChange={e => setEditForm(s => ({ ...s, note: e.target.value }))}
                  data-testid="edit-clock-note"
                  rows={2}
                  placeholder="Contoh: absen manual karena HP mati / lupa clock-out setelah lembur..."
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none" />
              </label>
              {editHistory.length > 0 && (
                <details className="text-[11px]">
                  <summary className="cursor-pointer text-violet-700 font-medium">
                    Riwayat Edit ({editHistory.length})
                  </summary>
                  <div className="mt-2 space-y-1.5 max-h-40 overflow-auto">
                    {editHistory.map(h => (
                      <div key={h.id} className="border border-slate-200 rounded p-2 bg-slate-50">
                        <p className="text-slate-700 font-medium">{h.edited_by} · <span className="text-slate-500">{formatTanggal(h.edited_at, { denganJam: true })}</span></p>
                        {(h.changes || []).map((c, i) => (
                          <p key={i} className="text-slate-600 font-mono text-[10px]">
                            {c.field}: {isoToHHMM(c.from) || "—"} → {isoToHHMM(c.to) || "—"}
                          </p>
                        ))}
                        {h.note && <p className="italic text-slate-500 mt-0.5">&ldquo;{h.note}&rdquo;</p>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setEditModal(null)} disabled={editSaving}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                  Batal
                </button>
                <button onClick={saveEditClock} disabled={editSaving}
                  data-testid="btn-save-edit-clock"
                  className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50">
                  <Save className="w-4 h-4" /> {editSaving ? "Menyimpan..." : "Simpan Bypass"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
